import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input, signal } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { finalize, Observable } from 'rxjs';

import { ConfirmIcon, ConfirmVariant } from './confirm.types';

@Component({
    selector: 'app-confirm-dialog',
    standalone: true,
    imports: [CommonModule, TranslocoDirective],
    templateUrl: './confirm-dialog.component.html',
    styleUrl: './confirm-dialog.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfirmDialogComponent {
    protected readonly modal = inject(NgbActiveModal);
    private readonly transloco = inject(TranslocoService);

    @Input() title = 'common.confirm.title';
    @Input() message = 'common.confirm.message';
    @Input() params: Record<string, unknown> | undefined;
    @Input() confirmLabel = 'common.confirm.confirm';
    @Input() cancelLabel = 'common.cancel';
    @Input() hideCancel = false;
    @Input() saveLabel = 'common.save';
    @Input() variant: ConfirmVariant = 'primary';
    @Input() icon: ConfirmIcon = 'warning';

    @Input() confirmAction?: () => Observable<unknown>;
    @Input() saveAction?: () => Observable<boolean>;

    protected readonly pendingAction = signal<'confirm' | 'save' | null>(null);

    protected readonly loading = () => this.pendingAction() !== null;

    protected confirm(): void {
        if (this.pendingAction()) return;

        if (!this.confirmAction) {
            this.modal.close(true);
            return;
        }

        this.pendingAction.set('confirm');
        this.confirmAction()
            .pipe(finalize(() => this.pendingAction.set(null)))
            .subscribe({
                next: (result) => this.modal.close(result ?? true),
                error: () => undefined,
            });
    }

    protected save(): void {
        if (!this.saveAction || this.pendingAction()) return;

        this.pendingAction.set('save');
        this.saveAction()
            .pipe(finalize(() => this.pendingAction.set(null)))
            .subscribe({
                next: (saved) => {
                    if (saved) {
                        this.modal.close(true);
                    }
                },
                error: () => undefined,
            });
    }

    protected cancel(): void {
        if (this.pendingAction()) return;
        this.modal.dismiss(false);
    }

    protected text(value: string | undefined, fallbackKey: string): string {
        const raw = value ?? fallbackKey;
        const translated = this.transloco.translate<string>(raw, this.params);
        return translated && translated !== raw ? translated : raw;
    }
}
